// ─── Central Shopify Config ─────────────────────────────────────────────────
// Single source of truth for all Shopify Storefront credentials.
// Priority: 1) import.meta.env  2) window.NATHOF_ENV  3) hardcoded fallback
// storeDomain is NEVER allowed to be empty.

const env = import.meta.env as Record<string, string | undefined>;
const runtime =
  (typeof window !== 'undefined' && (window as any).NATHOF_ENV) as
    | Record<string, string | undefined>
    | undefined;

// ── Helpers ─────────────────────────────────────────────────────────────────
function sanitizeDomain(raw: string): string {
  return raw.replace(/^https?:\/\//, '').replace(/\/+$/, '');
}

type EnvSource = 'build' | 'runtime' | 'fallback';

function resolve(
  buildKeys: string[],
  runtimeKey: string,
  fallback: string,
): { value: string; source: EnvSource } {
  for (const k of buildKeys) {
    const v = env[k];
    if (v) return { value: v, source: 'build' };
  }
  const r = runtime?.[runtimeKey];
  if (r) return { value: r, source: 'runtime' };
  return { value: fallback, source: 'fallback' };
}

// ── Domain ──────────────────────────────────────────────────────────────────
const FALLBACK_DOMAIN = 'nathof.myshopify.com';

const domainResolution = resolve(
  ['VITE_SHOPIFY_STORE_DOMAIN'],
  'VITE_SHOPIFY_STORE_DOMAIN',
  FALLBACK_DOMAIN,
);
export const SHOPIFY_STORE_DOMAIN = sanitizeDomain(domainResolution.value);
export const SHOPIFY_DOMAIN_SOURCE: EnvSource = domainResolution.source;

// ── Token ───────────────────────────────────────────────────────────────────
const tokenResolution = resolve(
  ['VITE_SHOPIFY_STOREFRONT_ACCESS_TOKEN', 'VITE_SHOPIFY_STOREFRONT_TOKEN'],
  'VITE_SHOPIFY_STOREFRONT_ACCESS_TOKEN',
  '',
);
export const SHOPIFY_STOREFRONT_TOKEN = tokenResolution.value;
export const SHOPIFY_TOKEN_SOURCE: EnvSource = tokenResolution.source;

// ── API ─────────────────────────────────────────────────────────────────────
export const SHOPIFY_API_VERSION = '2025-01';

export function getShopifyEndpoint(): string {
  return `https://${SHOPIFY_STORE_DOMAIN}/api/${SHOPIFY_API_VERSION}/graphql.json`;
}

export function isShopifyConfigured(): boolean {
  return !!SHOPIFY_STOREFRONT_TOKEN && !!SHOPIFY_STORE_DOMAIN;
}

// ── Diagnostics ─────────────────────────────────────────────────────────────
export function getShopifyDiagnostics() {
  const tokenMasked = SHOPIFY_STOREFRONT_TOKEN
    ? `${SHOPIFY_STOREFRONT_TOKEN.slice(0, 4)}…${SHOPIFY_STOREFRONT_TOKEN.slice(-4)}`
    : '(empty)';

  return {
    domain: SHOPIFY_STORE_DOMAIN,
    domainSource: SHOPIFY_DOMAIN_SOURCE,
    tokenStatus: SHOPIFY_STOREFRONT_TOKEN ? 'OK' : 'MISSING',
    tokenSource: SHOPIFY_TOKEN_SOURCE,
    tokenMasked,
    endpoint: getShopifyEndpoint(),
    configured: isShopifyConfigured(),
  };
}

// ── Observability: warn if fallback is in use ───────────────────────────────
if (SHOPIFY_DOMAIN_SOURCE === 'fallback') {
  console.warn(
    '[NAT HOF] Shopify ENV fallback em uso – verificar Cloud Secrets\n' +
      `  domain=${SHOPIFY_STORE_DOMAIN} (source: fallback)`,
  );
}
if (SHOPIFY_TOKEN_SOURCE === 'fallback' || !SHOPIFY_STOREFRONT_TOKEN) {
  console.warn(
    '[NAT HOF] Shopify token ausente ou em fallback – verificar Cloud Secrets',
  );
}

// ── Error buffer (shared) ───────────────────────────────────────────────────
interface ShopifyError {
  timestamp: string;
  message: string;
  status?: number;
}

const errorBuffer: ShopifyError[] = [];
const MAX_ERRORS = 10;

export function recordShopifyError(message: string, status?: number) {
  errorBuffer.unshift({ timestamp: new Date().toISOString(), message, status });
  if (errorBuffer.length > MAX_ERRORS) errorBuffer.pop();
}

export function getRecentErrors(): ShopifyError[] {
  return errorBuffer.slice(0, 3);
}

// ── Health check state ──────────────────────────────────────────────────────
export type HealthStatus = 'pending' | 'ok' | 'failed' | 'unconfigured';

let healthStatus: HealthStatus = isShopifyConfigured() ? 'pending' : 'unconfigured';
let healthMessage = '';
let healthShopName = '';

export function setHealthResult(
  status: HealthStatus,
  message: string,
  shopName?: string,
) {
  healthStatus = status;
  healthMessage = message;
  if (shopName) healthShopName = shopName;
}

export function getHealthState() {
  return { status: healthStatus, message: healthMessage, shopName: healthShopName };
}
